import { useQuery } from '@tanstack/react-query';
import type { CombinedQuerySuggestion, ContentQuerySuggestion } from '../@types/QuerySuggestion';
import type { RequestStateQuery } from '../@types/RequestStateQuery';
import type { ApiResponse } from '../@types/api-response';
import { SUGGESTION_QUERY_COMBINED, SUGGESTION_QUERY_CONTENT } from '../Data/Article/QueryKeys';

export const useCombinedSuggestion = (
  query?: string,
): [CombinedQuerySuggestion | undefined, RequestStateQuery] => {
  const { data, isPending, isSuccess, isError } = useQuery<ApiResponse<CombinedQuerySuggestion>>({
    queryKey: [SUGGESTION_QUERY_COMBINED, query],
    queryFn: async () => {
      const response = await fetch(`${SUGGESTION_QUERY_COMBINED}?q=${encodeURIComponent(query!)}`);
      return await response.json();
    },
    retry: false,
    refetchOnWindowFocus: false,
    gcTime: Number.POSITIVE_INFINITY,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: (query?.length || 0) > 2,
  });

  return [
    (query?.length || 0) > 2 ? data?.response : undefined,
    { isLoading: isPending, isSuccess, isError },
  ];
};

export const useContentSuggestion = (
  query?: string,
): [ContentQuerySuggestion[] | undefined, RequestStateQuery] => {
  const { data, isPending, isSuccess, isError } = useQuery<ApiResponse<ContentQuerySuggestion[]>>({
    queryKey: [SUGGESTION_QUERY_CONTENT, query],
    queryFn: async () => {
      const response = await fetch(`${SUGGESTION_QUERY_CONTENT}?q=${encodeURIComponent(query!)}`);
      return await response.json();
    },
    retry: false,
    refetchOnWindowFocus: false,
    gcTime: Number.POSITIVE_INFINITY,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: (query?.length || 0) > 2,
  });

  return [
    (query?.length || 0) > 2 ? data?.response : undefined,
    { isLoading: isPending, isSuccess, isError },
  ];
};
